<template>
  <div class="stk-matout">
    <!-- 头部顶栏与搜索区 -->
    <header class="query-header" role="banner" aria-label="生产领料出库">
      <button
        type="button"
        class="back-button"
        aria-label="返回功能首页"
        :disabled="busy"
        @click="onClickLeft"
      >
        <van-icon name="arrow-left" size="20" aria-hidden="true" />
      </button>

      <form class="search-form" role="search" @submit.prevent="onSearch">
        <div class="search-input">
          <van-icon name="search" size="14" aria-hidden="true" />
          <input
            v-model="v_search"
            :disabled="busy || listLoading"
            placeholder="销售订单 / 领料部门 / 出库单号"
            type="search"
            enterkeyhint="search"
            aria-label="搜索销售订单、领料部门或出库单号"
          />
          <button
            v-if="v_search"
            type="button"
            class="clear-button"
            aria-label="清空搜索"
            @click="clearSearch"
          >
            <van-icon name="cross" size="14" />
          </button>
        </div>

        <button
          type="submit"
          class="header-action-button"
          aria-label="查询"
          :disabled="listLoading || busy"
        >
          <van-icon name="search" size="16" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="header-action-button"
          aria-label="刷新"
          :disabled="listLoading || busy"
          @click="onSearch"
        >
          <van-icon
            name="replay"
            size="16"
            :class="{ 'is-spinning': listLoading }"
            aria-hidden="true"
          />
        </button>
      </form>
    </header>

    <!-- 主列表滚动区域：全量平滑滚动，无分页器 -->
    <main
      ref="listContent"
      class="list-content"
      :aria-label="v_title"
      :aria-busy="listLoading"
    >
      <div v-if="listError" class="state-panel" role="alert">
        <p>{{ listError }}</p>
        <van-button class="primary-button" :disabled="busy || listLoading" @click="onSearch">重新查询</van-button>
      </div>
      <div v-else-if="listLoading && !allData.length" class="state-panel" role="status">
        <van-loading size="24px" />
        <span>正在查询未领料出库单…</span>
      </div>
      <div v-else-if="!allData.length" class="state-panel" role="status">
        <van-icon name="description" size="40" color="#999999" />
        <p>暂无未领料出库单据</p>
      </div>
      <template v-else>
        <article
          v-for="(item, index) in allData"
          :key="item.IDSEQ"
          class="chk-card"
          :aria-label="'出库单 ' + item.IDNO"
        >
          <!-- 卡片头部：左侧序号、单号与分类，右侧状态标签 -->
          <header class="card-header">
            <div class="card-title-group">
              <span class="card-seq">{{ index + 1 }}</span>
              <span class="card-tag">出库单</span>
              <h2 class="card-idno">{{ item.IDNO || '—' }}</h2>
            </div>
            <span class="status-tag" :class="item.STFG === 'Y' ? 'is-audited' : 'is-pending'">
              {{ item.STFG === 'Y' ? '已审核' : '未审核' }}
            </span>
          </header>

          <!-- 单据核心信息区：现代弹性网格，杜绝任何挤压与重叠 -->
          <dl class="document-info">
            <div>
              <dt>出库日期</dt>
              <dd>{{ item.ICDTTM || '—' }}</dd>
            </div>
            <div>
              <dt>销售订单</dt>
              <dd>{{ item.ORDNO || '—' }}</dd>
            </div>
            <div class="full-row">
              <dt>投料单号</dt>
              <dd class="highlight-text">{{ item.PICKNO || '—' }}</dd>
            </div>
            <div class="full-row">
              <dt>产品编码</dt>
              <dd>{{ item.MATNO || '—' }}</dd>
            </div>
            <div class="full-row">
              <dt>产品名称</dt>
              <dd>{{ item.MATNM || '—' }}</dd>
            </div>
            <div class="full-row">
              <dt>领料部门</dt>
              <dd>{{ item.WKNM || '—' }}</dd>
            </div>

            <!-- 出库项数同行右侧放置【展开 / 收起】微胶囊按钮 -->
            <div class="full-row count-and-expand-row">
              <div class="count-item-wrap">
                <dt>出库项数</dt>
                <dd class="count-tag-wrap">
                  <span class="count-badge">{{ item.ROWCNT == null ? '0' : item.ROWCNT }} 项</span>
                </dd>
              </div>
              <button
                type="button"
                class="toggle-expand-btn"
                :aria-expanded="isExpanded(item)"
                @click="toggleExpand(item)"
              >
                <span>{{ isExpanded(item) ? '收起' : '展开' }}</span>
                <van-icon :name="isExpanded(item) ? 'arrow-up' : 'arrow-down'" />
              </button>
            </div>
          </dl>

          <!-- 就地展开 (Inline Expand) 的出库物料明细清单 -->
          <section
            v-if="isExpanded(item)"
            class="expanded-details"
            :aria-label="'出库单 ' + item.IDNO + ' 物料明细'"
          >
            <div class="details-header">
              <span class="details-title">出库物料明细</span>
              <span class="details-badge">共 {{ detailsFor(item).length }} 项</span>
            </div>

            <!-- 明细加载状态 -->
            <div v-if="dtlLoadingMap[String(item.IDSEQ)]" class="state-panel-inline">
              <van-loading size="20px">正在读取出库明细…</van-loading>
            </div>

            <!-- 空明细提示 -->
            <div
              v-else-if="!detailsFor(item).length"
              class="empty-dtl-notice"
            >
              <van-icon name="info-o" color="#999999" size="18" />
              <span>暂无出库物料明细记录</span>
            </div>

            <!-- 物料明细紧凑子卡片列表 -->
            <div v-else class="dtl-card-list">
              <article
                v-for="(row, rowIdx) in detailsFor(item)"
                :key="(row.IDSEQ || item.IDSEQ) + '_' + (row.LSTSEQ || rowIdx)"
                class="dtl-item-card"
              >
                <!-- 物料行头部 -->
                <header class="dtl-heading">
                  <span class="dtl-seq-badge">{{ rowIdx + 1 }}</span>
                  <div class="dtl-mat-info">
                    <div class="dtl-mat-title">
                      <span class="dtl-mat-no">{{ row.MATNO || '—' }}</span>
                      <span class="dtl-mat-nm">{{ row.MATNM || '未命名物料' }}</span>
                    </div>
                    <p class="dtl-mat-spec">{{ row.MATDESC || '暂无规格描述' }}</p>
                  </div>
                </header>

                <!-- 数据网格：出库数、仓库、仓位 -->
                <div class="dtl-meta-grid">
                  <div class="meta-item highlight-item">
                    <span class="meta-label">出库数量</span>
                    <span class="meta-value highlight-val">{{ row.ACTQTY == null || row.ACTQTY === '' ? (row.STKQTY || 0) : row.ACTQTY }} {{ row.POUNIT || row.UNITNM || '' }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">仓库名称</span>
                    <span class="meta-value">{{ row.STKNM || '—' }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">仓位名称</span>
                    <span class="meta-value">{{ row.PLCNM || '—' }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">采购数量</span>
                    <span class="meta-value">{{ row.POQTY == null || row.POQTY === '' ? '—' : row.POQTY }}</span>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <!-- 卡片底部操作按钮区域（从右往左排列，紧凑实色微胶囊） -->
          <footer class="card-footer-actions">
            <van-button
              v-if="item.STFG === 'N'"
              type="danger"
              size="small"
              class="action-btn discard-btn"
              :disabled="busy || listLoading"
              @click="stfgRec(item, 'X')"
            >
              作废
            </van-button>
            <van-button
              v-if="item.STFG === 'N'"
              type="primary"
              size="small"
              class="action-btn audit-btn"
              :disabled="cfmBool || busy || listLoading"
              :loading="auditingId === String(item.IDSEQ)"
              @click="stfgRec(item, 'Y')"
            >
              审核
            </van-button>
            <van-button
              v-if="item.STFG === 'Y'"
              type="danger"
              size="small"
              class="action-btn abandon-btn"
              :disabled="cfmBool || busy || listLoading"
              @click="stfgRec(item, 'N')"
            >
              弃审
            </van-button>
          </footer>
        </article>
      </template>
    </main>

    <!-- 返回顶部按钮 -->
    <div v-show="topShow" class="go_top_btn" @click="goTop">
      <van-icon name="arrow-up" size="24px" color="#235EFF" />
    </div>
  </div>
</template>

<script>
import { getDate, getDate2 } from '@/api/tools'

export default {
  name: 'StkMatOut',
  data() {
    return {
      v_title: '生产领料出库',
      v_search: '',
      listLoading: false,
      listError: '',
      auditingId: '',

      v_usernm: this.$cookies.get('v_usernm'),
      v_username: this.$cookies.get('v_telno'),
      v_corpid: this.$cookies.get('v_corpid'),

      allData: [],

      // 就地展开与明细缓存
      expandedIds: [],
      detailsMap: {},
      dtlLoadingMap: {},

      // 权限状态
      editBool: false,
      cfmBool: false,
      cancelBool: false,

      // 返回顶部
      topShow: false,
      scroll: 0,
    }
  },
  computed: {
    busy() {
      return !!this.auditingId || this.listLoading
    },
  },
  mounted() {
    window.addEventListener('scroll', this.windowScroll, true)
    this.initData()
    this.onSearch()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.windowScroll, true)
  },
  methods: {
    clearSearch() {
      this.v_search = ''
      this.onSearch()
    },

    isExpanded(item) {
      return this.expandedIds.indexOf(String(item.IDSEQ)) >= 0
    },

    detailsFor(item) {
      return this.detailsMap[String(item.IDSEQ)] || []
    },

    // 展开/收起切换
    toggleExpand(item) {
      const id = String(item.IDSEQ)
      const idx = this.expandedIds.indexOf(id)
      if (idx >= 0) {
        this.expandedIds.splice(idx, 1)
      } else {
        this.expandedIds.push(id)
        if (!this.detailsMap[id] || !this.detailsMap[id].length) {
          this.getDtlList(item.IDSEQ)
        }
      }
    },

    // 异步加载出库明细 (V_IC_STKDTL_MIN)
    async getDtlList(v_idseq) {
      const id = String(v_idseq)
      this.$set(this.dtlLoadingMap, id, true)

      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'idseq',
            p_value: v_idseq,
            p_table: 'V_IC_STKDTL_MIN',
            p_cond: ' order by MATNO',
          },
        })

        if (res && String(res.code) === '200' && Array.isArray(res.result)) {
          this.$set(this.detailsMap, id, res.result)
        } else {
          this.$dialog.alert({
            title: '提示',
            message: (res && res.result) || '获取物料明细失败',
            theme: 'round-button',
          })
        }
      } catch (err) {
        this.$toast.fail('明细加载失败：' + (err.message || '网络异常'))
      } finally {
        this.$set(this.dtlLoadingMap, id, false)
      }
    },

    // 状态变更：审核 ('Y') / 弃审 ('N') / 作废 ('X')
    stfgRec(item, val) {
      if (this.busy) return
      const commText = val === 'Y' ? '审核' : val === 'N' ? '弃审' : '作废'

      this.$dialog
        .confirm({
          title: '提示',
          message: '你确定要' + commText + '此单据吗?',
          confirmButtonColor: val === 'Y' ? '#235EFF' : '#ee0a24',
          confirmButtonText: '确认',
          cancelButtonText: '取消',
        })
        .then(async () => {
          this.auditingId = String(item.IDSEQ)
          let v_cond = ''
          if (val === 'Y') {
            const v_chkdttm = getDate('full')
            const v_chkuser = this.v_usernm || ''
            v_cond = " set stfg='" + val + "',chkuser='" + v_chkuser + "',chkdttm='" + v_chkdttm + "'"
          } else {
            v_cond = " set stfg='" + val + "',chkuser='',chkdttm=''"
          }

          const loadingToast = this.$toast.loading({
            message: commText + '处理中...',
            forbidClick: true,
            duration: 0,
            loadingType: 'spinner',
          })

          try {
            const res = await this.$axios({
              method: 'post',
              url: 'xuper_auth/updateRec',
              data: {
                p_key: 'idseq',
                p_value: item.IDSEQ,
                p_table: 'ic_stk',
                p_cond: v_cond,
              },
            })

            if (res && String(res.code) === '200') {
              this.$toast.success(commText + '成功')
              // 移除展开状态
              const expIdx = this.expandedIds.indexOf(String(item.IDSEQ))
              if (expIdx >= 0) this.expandedIds.splice(expIdx, 1)
              await this.onSearch()
            } else {
              this.$toast({
                type: 'html',
                icon: 'warning-o',
                message: (res && res.result) || commText + '失败',
                size: 'large',
                duration: 3000,
              })
            }
          } catch (err) {
            this.$dialog.alert({
              title: '操作失败',
              message: err.message || commText + '出现异常，请重试',
            })
          } finally {
            loadingToast.clear()
            this.auditingId = ''
          }
        })
        .catch(() => {})
    },

    // 搜索查询：硬性锁定未领料 (stfg='N')，全量平滑滚动
    async onSearch() {
      if (this.listLoading) return
      this.listLoading = true
      this.listError = ''

      // 强制锁定未领料 / 未审核过滤条件
      let v_cond = " and stfg='N'"
      if (this.v_corpid) {
        v_cond += ' and corpid=' + this.v_corpid
      }
      v_cond += " and adddttm>='" + getDate2(30) + "'"

      if (this.v_search && this.v_search.trim()) {
        const v_val = this.v_search.trim().toUpperCase()
        v_cond +=
          " and (ordno like '%" +
          v_val +
          "%' or idno like '%" +
          v_val +
          "%' or wknm like '%" +
          v_val +
          "%' or adduser like '%" +
          v_val +
          "%')"
      }

      try {
        const res = await this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_cond: v_cond + ' order by adddttm desc',
            p_table: 'V_IC_STK_PRDOUT',
          },
        })

        if (res && String(res.code) === '200' && Array.isArray(res.result)) {
          this.allData = res.result
        } else {
          this.allData = []
          this.listError = (res && res.result) || '未查询到相关数据'
        }
      } catch (err) {
        this.allData = []
        this.listError = err.message || '网络连接异常，查询失败'
      } finally {
        this.listLoading = false
      }
    },

    // 初始化页面与用户权限
    initData() {
      if (this.$route && this.$route.query && this.$route.query.path) {
        this.$axios({
          method: 'post',
          url: 'xuper_auth/getBaseData',
          data: {
            p_key: 'isqry',
            p_value: 'M',
            p_key2: 'frmurl',
            p_value2: this.$route.query.path,
            p_table: 'V_Sys_form',
          },
        }).then(res => {
          if (res && res.result && res.result.length > 0) {
            this.v_title = res.result[0].CFRMNM || '生产领料出库'
          }
        })
      }

      // 获取用户权限
      this.$axios({
        method: 'post',
        url: 'xuper_auth/getBaseData',
        data: {
          p_key: 'userno',
          p_value: this.v_username,
          p_key2: 'frmidseq',
          p_value2: '419845',
          p_table: 'v_sys_user_priv',
        },
      }).then(res => {
        if (res && res.result && res.result.length > 0) {
          const row = res.result[0]
          this.editBool = row.ISUPD === 'N'
          this.cfmBool = row.ISCFM === 'N'
          this.cancelBool = row.ISCANCEL === 'N'
        }
      })
    },

    onClickLeft() {
      this.$router.push({ path: '/features' })
    },

    windowScroll(e) {
      const target = (e && e.target) || document.documentElement || document.body
      const top = target.scrollTop || 0
      this.topShow = top > 260
    },

    goTop() {
      const container = this.$refs.listContent
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/scss/variables.scss';

.stk-matout {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #f7f8fa;
  color: $text-primary;
  font: 14px/1.45 $font-family-base;
  overflow: hidden;
  box-sizing: border-box;
}

// 头部顶栏
.query-header {
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 48px;
  padding: 4px 8px;
  background: #ffffff;
  border-bottom: 1px solid #ebedf0;
  z-index: 10;
}

.back-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: $text-primary;

  &:disabled {
    color: $text-tertiary;
  }
}

.search-form {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 6px;
}

.search-input {
  min-width: 0;
  flex: 1;
  height: 32px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  border: 1px solid $border-color;
  border-radius: 16px;
  background: #f7f8fa;
  color: $text-secondary;

  &:focus-within {
    border-color: $primary-color;
    background: #ffffff;
  }

  input {
    width: 0;
    min-width: 0;
    flex: 1;
    border: 0;
    background: transparent;
    color: $text-primary;
    font-size: 13px;
    outline: none;

    &::placeholder {
      color: $text-tertiary;
    }
  }
}

.clear-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  color: $text-tertiary;
}

.header-action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #ffffff;
  color: $primary-color;

  &:active {
    background: #f2f3f5;
  }

  &:disabled {
    color: $text-tertiary;
    border-color: #ebeef5;
    background: #fafafa;
  }
}

.is-spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// 主滚动内容区
.list-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
}

.state-panel {
  min-height: 180px;
  padding: 28px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  text-align: center;
  color: $text-secondary;
}

// 主单卡片（紧凑微胶囊对标）
.chk-card {
  max-width: 760px;
  margin: 0 auto 6px;
  padding: 8px 10px;
  border: 1px solid #ebedf0;
  border-radius: 6px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 6px;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  flex: 1;
}

.card-seq {
  flex: none;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  background: #f0f2f5;
  color: $text-secondary;
}

.card-tag {
  flex: none;
  padding: 0 4px;
  border-radius: 3px;
  font-size: 10px;
  line-height: 16px;
  background: #edf2ff;
  color: $primary-color;
  font-weight: 500;
}

.card-idno {
  min-width: 0;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  overflow-wrap: anywhere;
  word-break: break-all;
}

// 右上角状态标签
.status-tag {
  flex: none;
  border-radius: 3px;
  padding: 0 5px;
  font-size: 11px;
  line-height: 18px;
  font-weight: 500;
}

.is-pending {
  background: #fff0ed;
  color: #ff4000;
  border: 1px solid #ffd6cc;
}

.is-audited {
  background: #edf2ff;
  color: $primary-color;
}

// 展开/收起微胶囊按钮
.toggle-expand-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 22px;
  padding: 0 6px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  background: #f8f9fa;
  color: $primary-color;
  font-size: 11px;
  font-weight: 500;
  flex: none;

  &:active {
    background: #ebedf0;
  }
}

// 单据核心信息区（自适应网格，杜绝溢出）
.document-info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 8px;
  margin: 0;
  font-size: 12px;
  line-height: 19px;

  > div {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr);
    align-items: baseline;
    gap: 4px;
    min-width: 0;
  }

  dt {
    font-size: 11.5px;
    color: $text-secondary;
    white-space: nowrap;
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
    word-break: break-all;
    color: $text-primary;
    font-size: 12px;
  }
}

.full-row {
  grid-column: 1 / -1;
}

.highlight-text {
  font-weight: 600;
  color: #103b9a;
}

.count-and-expand-row {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: 1px;
}

.count-item-wrap {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.count-tag-wrap {
  display: flex;
  align-items: center;
}

.count-badge {
  display: inline-block;
  padding: 0 5px;
  border-radius: 3px;
  background: #f0f5ff;
  color: $primary-color;
  font-weight: 600;
  font-size: 11px;
  line-height: 16px;
}

// 就地展开容器
.expanded-details {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #dcdfe6;
}

.details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
}

.details-title {
  font-size: 12px;
  font-weight: 600;
  color: $text-primary;
}

.details-badge {
  font-size: 11px;
  color: $text-secondary;
}

.state-panel-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  color: $text-secondary;
  font-size: 12px;
}

.empty-dtl-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 6px;
  background: #f8fafc;
  border-radius: 4px;
  color: $text-secondary;
  font-size: 11.5px;
}

// 物料明细子卡片
.dtl-card-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dtl-item-card {
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  background: #f8fafc;
}

.dtl-heading {
  display: flex;
  gap: 5px;
  align-items: flex-start;
}

.dtl-seq-badge {
  width: 16px;
  height: 16px;
  line-height: 16px;
  flex: none;
  border-radius: 2px;
  text-align: center;
  background: #edf2ff;
  color: $primary-color;
  font-size: 10px;
  font-weight: 600;
  margin-top: 1px;
}

.dtl-mat-info {
  flex: 1;
  min-width: 0;
}

.dtl-mat-title {
  display: flex;
  flex-wrap: wrap;
  gap: 3px 5px;
  align-items: baseline;
}

.dtl-mat-no {
  font-size: 12px;
  font-weight: 600;
  color: #1a202c;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.dtl-mat-nm {
  font-size: 12px;
  color: $text-primary;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.dtl-mat-spec {
  margin: 1px 0 0;
  font-size: 11px;
  color: $text-secondary;
  line-height: 15px;
  overflow-wrap: anywhere;
  word-break: break-all;
}

// 明细数据网格
.dtl-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 8px;
  margin: 4px 0;
  padding: 4px 6px;
  background: white;
  border-radius: 4px;
  border: 1px solid #edf2f7;
}

.meta-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11.5px;
  line-height: 18px;
  min-width: 0;
}

.meta-label {
  color: $text-secondary;
  white-space: nowrap;
}

.meta-value {
  font-weight: 500;
  color: $text-primary;
  overflow-wrap: anywhere;
  word-break: break-all;
  font-variant-numeric: tabular-nums;
}

.highlight-item {
  .highlight-val {
    font-weight: 700;
    color: #103b9a;
  }
}

// 卡片底部操作按钮区域（从右往左排列，紧凑实色微胶囊）
.card-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f0f2f5;
}

.action-btn {
  flex: none;
  min-width: 66px;
  height: 26px;
  line-height: 24px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 13px;
  color: #fff !important;
}

.audit-btn {
  background: #1ecd84 !important;
  border-color: #1ecd84 !important;

  &:active {
    background: #12a66a !important;
    border-color: #12a66a !important;
  }
}

.discard-btn {
  background: #ff4d4f !important;
  border-color: #ff4d4f !important;

  &:active {
    background: #d9363e !important;
    border-color: #d9363e !important;
  }
}

.abandon-btn {
  background: #ff7875 !important;
  border-color: #ff7875 !important;

  &:active {
    background: #d9534f !important;
    border-color: #d9534f !important;
  }
}

// 返回顶部按钮
.go_top_btn {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 50%;
  border: 1px solid #dcdcdc;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: fixed;
  right: 16px;
  bottom: 24px;
  z-index: 99;
  cursor: pointer;
}
</style>
